import { FC, useMemo } from 'react';
import {
  CalculationErrorType,
  formatCodeName,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  Detail,
  useSystemClassifierSelectOptions,
  formatDate,
  CodeBlock,
  translateProcessingError,
} from '@atlas-frontend/sdk';
import { Button, Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './CalculationErrorDetailsModal.translations.json';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';

const { addTranslations, useTranslation } = i18nNsFactory(
  'calculation-error-details-modal',
);
addTranslations(translations);

type Props = {
  error: CalculationErrorType;
  onClose: () => void;
};

export const CalculationErrorDetailsModal: FC<Props> = ({ error, onClose }) => {
  const { t } = useTranslation();

  const { PROCESSING_ERROR_TYPE: processingErrorTypes } =
    useSystemClassifierSelectOptions(
      ['PROCESSING_ERROR_TYPE'],
      ({ id, name }) => ({ id, name }),
    );

  const errorType = useMemo(() => {
    const type = error?.typeId;
    return processingErrorTypes.find((errorType) => errorType.id === type);
  }, [error, processingErrorTypes]);

  return (
    <Modal
      maskClosable={false}
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <Button id="close-modal" type="primary" onClick={onClose}>
              {t('button.close')}
            </Button>
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="80rem"
    >
      <ModalHeader>
        <ModalTitle>{t('title')}</ModalTitle>
      </ModalHeader>
      {error && (
        <Padding scrollable>
          <Flex vertical gap="large">
            <Flex vertical gap="small">
              <h5>{t('mainData')}</h5>
              <Detail label={t('dataObjectDescription')}>
                {formatCodeName({
                  code: error.dataObjectDescriptionCode,
                  name: error.dataObjectDescriptionName,
                })}
              </Detail>
              <Detail label={t('valueDate')}>
                {error?.valueDate ? formatDate(error.valueDate) : null}
              </Detail>
              <Detail label={t('algorithm')}>
                {error.dataObjectDescriptionVersionId && error.algorithmId ? (
                  <UiLink
                    id={`output-description-${error.dataObjectDescriptionVersionId}-algorithm-${error.algorithmId}`}
                    path={path.outputDataObjectView}
                    params={{
                      id: error.dataObjectDescriptionVersionId,
                      segment: 'algorithms',
                      algorithmId: error.algorithmId,
                    }}
                  >
                    {error.algorithmCode}
                  </UiLink>
                ) : (
                  error.algorithmCode
                )}
              </Detail>
              <Detail label={t('errorType')}>{errorType?.name}</Detail>
            </Flex>
            <Flex vertical gap="small">
              <h5>{t('description')}</h5>
              <CodeBlock>{translateProcessingError(error)}</CodeBlock>
            </Flex>
          </Flex>
        </Padding>
      )}
    </Modal>
  );
};
